import React, { memo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Package } from 'types/interfaces/Package';

import { useValidationRules } from 'hooks/useValidationRules';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Legal } from 'components/sections/BuySticker/Legal';
import { CvvInput } from 'components/shared/CvvInput';

import css from './buyStickerPopupContent.module.sass';

interface Props {
  isLoading: boolean;
  isEnabledCvvField: boolean;
  stickerPackage: Package;
  defaultPackPreviewUrl?: string;
  defaultStickerPreviewUrl?: string;
  onSubmit: (newValues: Record<string, any>) => void;
}

export const BuyStickerPopupContent: React.FC<Props> = memo(
  ({
    isLoading,
    isEnabledCvvField,
    stickerPackage,
    defaultPackPreviewUrl,
    defaultStickerPreviewUrl,
    onSubmit,
  }) => {
    const { t } = useTranslation(['common', 'app']);

    const { control, handleSubmit, errors } = useForm();
    const { requiredValidation } = useValidationRules();

    return (
      <>
        <h2 className={css.title}>{t('app:stickers.buy.title')}</h2>
        <p className={css.subtitle}>{t('app:stickers.buy.subtitle')}</p>
        <img
          className={css.image}
          src={defaultPackPreviewUrl || defaultStickerPreviewUrl}
        />

        <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
          {isEnabledCvvField && (
            <div className={css.cvv}>
              <Controller
                as={CvvInput}
                name="cvv"
                control={control}
                rules={{
                  ...requiredValidation,
                  maxLength: {
                    value: 3,
                    message: t('common:validations.cvvLength'),
                  },
                  minLength: {
                    value: 3,
                    message: t('common:validations.cvvLength'),
                  },
                }}
                error={errors.cvv?.message}
              />
            </div>
          )}

          <BaseButton
            nativeType="submit"
            type={ButtonTypes.Accent}
            className={css.sendBtn}
            loading={isLoading}
          >
            {t('app:stickers.buy.send')}
          </BaseButton>
        </form>
        <Legal className={css.legal} pack={stickerPackage} />
      </>
    );
  }
);
